export const prefersReducedMotion = (): boolean => {
	if (typeof window === "undefined") return false;
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export const smoothScrollBehavior = (): ScrollBehavior =>
	prefersReducedMotion() ? "auto" : "smooth";

export const smoothScrollTo = (elementId: string, offset: number = 0) => {
	const element = document.getElementById(elementId);
	if (element) {
		const elementPosition = element.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.pageYOffset - offset;

		window.scrollTo({
			top: offsetPosition,
			behavior: smoothScrollBehavior(),
		});
	}
};

export const scrollToSection = (sectionId: string) => {
	smoothScrollTo(sectionId, 80); // 5rem offset for header
};

/** Scroll an element into view so it sits below a fixed header (not behind it). */
export const scrollIntoViewWithOffset = (
	element: Element | null,
	offsetPx: number = 100
) => {
	if (!element) return;
	const top = element.getBoundingClientRect().top + window.scrollY - offsetPx;
	window.scrollTo({ top, behavior: smoothScrollBehavior() });
};

/**
 * Run scrollIntoView after layout has settled (double rAF). Calling it in the same
 * frame as an expand/collapse often measures pre-layout geometry, so the scroll
 * distance is tiny and feels like an instant jump instead of a smooth scroll.
 */
export function scheduleScrollIntoView(
	element: Element | null,
	options: ScrollIntoViewOptions = {}
): () => void {
	if (!element) return () => {};

	const { block = "start", inline = "nearest", behavior } = options;
	const scrollBehavior = behavior ?? smoothScrollBehavior();

	let raf0 = 0;
	let raf1 = 0;
	raf0 = requestAnimationFrame(() => {
		raf1 = requestAnimationFrame(() => {
			element.scrollIntoView({ block, inline, behavior: scrollBehavior });
		});
	});

	return () => {
		cancelAnimationFrame(raf0);
		cancelAnimationFrame(raf1);
	};
}
